import SecurePaymentV2 from 'assets/Icons/SecurePaymentV2';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useRef, useState } from 'react';
import {
  BiChevronRight,
  BiSolidStar,
  BiSolidStarHalf,
  BiX,
} from 'react-icons/bi';
import Carousel, { ResponsiveType } from 'react-multi-carousel';
import { useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import RenderIf from '@/components/Common/RenderIf';
import Button, {
  ButtonVariants,
} from '@/components/shared/basic/button/button';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { notify } from '@/components/shared/notify';

import { localUserID, user } from '@/atoms/user';
import { Endpoints } from '@/network';
import { generateUrlForImage } from '@/pages/books/[bookId]';
import { addToCart, addToRecentlyViewed } from '@/services/Cart';
import { AddToCartProps } from '@/utils/interfaces/cart';
import { IShowcaseShortVideo } from '@/utils/interfaces/ShowcaseShortVideo';
import { User } from '@/utils/interfaces/User';

interface ProductPricing {
  main_price: string;
  digital_price: string;
  digital_stroked_price: string;
  stroked_price: string;
  currency_symbol: string;
  preorder_price?: string | null;
  preorder_display_price?: string | null;
}

interface Props {
  showcaseShortVideos: IShowcaseShortVideo[];
  initialSlide?: number;
  onClose?: () => void;
}

const ShortVideoModel: FC<Props> = ({
  showcaseShortVideos,
  initialSlide = 0,
  onClose,
}) => {
  const [activeSlide, setActiveSlide] = useState(initialSlide);
  const carouselRef = useRef<any>(null);
  const [productPricingMap, setProductPricingMap] = useState<
    Record<string, ProductPricing>
  >({});
  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const twScreen = useTailwindBreakpoint();
  const isMobile = !twScreen.md;
  const [loadingAddToCart, setLoadingAddToCart] = useState({
    value: false,
    id: 0,
  });
  const localUserId = useRecoilValue(localUserID);
  const mobileContainerRef = useRef<HTMLDivElement>(null);

  // Sync the bytes query param when the active slide changes (no React re-render)
  const updateBytesQueryParam = (index: number) => {
    if (index >= 0 && index < showcaseShortVideos.length) {
      const videoId = showcaseShortVideos[index]?._id;
      if (videoId) {
        const url = new URL(window.location.href);
        if (url.searchParams.get('bytes') !== videoId) {
          url.searchParams.set('bytes', videoId);
          window.history.replaceState(null, '', url.pathname + url.search);
        }
      }
    }
  };

  const videosWithNull = isMobile
    ? showcaseShortVideos
    : [null, ...showcaseShortVideos];

  useEffect(() => {
    setActiveSlide(initialSlide);

    // Retry until carousel ref is ready (handles direct URL navigation)
    let attempts = 0;
    const maxAttempts = 20;
    const tryGoToSlide = () => {
      if (carouselRef.current) {
        carouselRef.current.goToSlide(initialSlide + 1, true);
      } else if (attempts < maxAttempts) {
        attempts++;
        requestAnimationFrame(tryGoToSlide);
      }
    };
    requestAnimationFrame(tryGoToSlide);
  }, [initialSlide]);

  const getStoredCountryData = () => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('zebra_selected_country');
        if (stored) {
          const parsed = JSON.parse(stored);
          return parsed?.value?.country || parsed?.country || parsed;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return null;
  };

  const getStoredCountry = (): string => {
    const countryData = getStoredCountryData();
    return countryData?.code || 'IN';
  };

  const fetchProductPricing = async (productId: string | number) => {
    try {
      const country = getStoredCountry();
      console.log({ country })
      const url = `${Endpoints.productDetail}/${productId}`;

      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${url}`, {
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          country: country,
        },
      });

      if (!response.ok) {
        throw new Error(`Error fetching product data: ${response.statusText}`);
      }

      const data = await response.json();
      const productData = data?.data;

      if (productData) {
        setProductPricingMap((prev) => ({
          ...prev,
          [productId]: {
            main_price: productData?.main_price || '',
            stroked_price: productData?.stroked_price || '',
            currency_symbol: productData?.currency_symbol || '₹',
            preorder_price: productData?.preorder_price || null,
            preorder_display_price: productData?.preorder_display_price || null,
            digital_price: productData.digital_pair.main_price,
            digital_stroked_price: productData.digital_pair.stroked_price,
          },
        }));
      }
    } catch (error) {
      console.error('Error fetching product pricing:', error);
    }
  };

  const handleAddDataToCart = async (
    payload: AddToCartProps,
    type: 'hardCopy' | 'digitalCopy',
    bookName: string,
  ) => {
    try {
      setLoadingAddToCart({ value: true, id: payload.id });

      await addToCart(payload);
      addToRecentlyViewed(payload.id, userData?.zl_uid);
      notify(
        `${bookName} ${type === 'hardCopy' ? 'Hard Copy' : 'Digital Copy'}  added to cart successfully!`,
        'success',
      );
      router.push(`/cart/`);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoadingAddToCart({ value: false, id: payload.id });
    }
  };

  const handleScroll = (e: any) => {
    const scrollTop = e.target.scrollTop;
    const height = window.innerHeight;

    const index = Math.round(scrollTop / height);
    setActiveSlide(index);
    updateBytesQueryParam(index);
  };

  useEffect(() => {
    if (activeSlide >= 0 && activeSlide < showcaseShortVideos.length) {
      const currentVideo = showcaseShortVideos[activeSlide];
      const productId = currentVideo?.bookId?.hardcoverProductId;

      if (productId && !productPricingMap[productId]) {
        fetchProductPricing(productId);
      }
    }
  }, [activeSlide, showcaseShortVideos]);

  useEffect(() => {
    if (!isMobile) return;

    // Retry until the scroll container ref is ready
    let attempts = 0;
    const maxAttempts = 20;
    const tryScroll = () => {
      if (mobileContainerRef.current) {
        mobileContainerRef.current.scrollTo({
          top: initialSlide * window.innerHeight,
          behavior: 'auto',
        });
      } else if (attempts < maxAttempts) {
        attempts++;
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
  }, [initialSlide, isMobile]);

  const CustomDot = ({ onClick, active }: any) => {
    return (
      <button
        onClick={onClick}
        className={`sm:w-4 w-3 sm:h-1 h-0.5 ${active ? 'bg-white' : 'bg-grayZl-850'
          }`}
      />
    );
  };

  const responsive: ResponsiveType = {
    br1: {
      breakpoint: { max: 10000, min: 0 },
      items: 1,
      slidesToSlide: 1,
    },
  };

  const getUrl = (slug: string, id: string) => {
    return `/books/${slug}-${id}/`;
  };

  const getEmbedUrl = (id: string, isActive: boolean) => {
    const params = new URLSearchParams({
      autoplay: isActive ? '1' : '0',
      mute: isActive ? '0' : '1',
      controls: '0',
      modestbranding: '1',
      rel: '0',
      playsinline: '1',
      enablejsapi: '1',
      fs: '0',
    });

    return `https://www.youtube.com/embed/${id}?${params.toString()}`;
  };

  const getThumbnailUrl = (id: string) =>
    `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // Hide YouTube shorts and share buttons
    const style = document.createElement('style');
    style.textContent = `
      iframe[src*="youtube.com"] button[aria-label*="Share"],
      iframe[src*="youtube.com"] button[aria-label*="Shorts"],
      iframe[src*="youtube.com"] [aria-label*="Share"],
      iframe[src*="youtube.com"] [aria-label*="Shorts"] {
        display: none !important;
      }
      .ytp-button[aria-label*="Share"],
      .ytp-button[aria-label*="Shorts"],
      button[aria-label="Share this video"],
      button[aria-label="Create Shorts"] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.body.style.overflow = '';
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  return (
    <div
      className='fixed inset-0 bg-black/90 z-50 flex items-center justify-center'
      style={{ height: '100dvh' }}
    >
      <div className='w-full h-full'>
        {isMobile ? (
          <div
            ref={mobileContainerRef}
            onScroll={handleScroll}
            className='h-full overflow-y-scroll snap-y snap-mandatory overflow-x-hidden no-scrollbar'
          >
            {videosWithNull.map((item, index) => {
              if (!item)
                return <div key='null' className='h-dvh snap-start'></div>;
              const showcaseShortVideo = item;
              const isActive = isMobile
                ? index === activeSlide
                : index - 1 === activeSlide;

              return (
                <div
                  key={showcaseShortVideo._id}
                  className='h-screen snap-start flex relative'
                >
                  {/* video */}
                  <div className='flex-1 relative bg-black '>
                    {isActive ? (
                      <iframe
                        src={getEmbedUrl(showcaseShortVideo.youtubeId, true)}
                        className='w-full h-full'
                        allow='autoplay; encrypted-media; picture-in-picture'
                        allowFullScreen
                      />
                    ) : (
                      <img
                        src={getThumbnailUrl(showcaseShortVideo.youtubeId)}
                        className='w-full h-full object-cover'
                      />
                    )}
                  </div>
                  <button
                    onClick={onClose}
                    className='absolute top-0 right-0 m-4 w-7 h-7 rounded-full bg-grayZl-500 flex items-center justify-center'
                  >
                    <BiX className='w-[18px] h-[18px] text-black' />
                  </button>
                  <div className='absolute bottom-0 left-0 px-4 pb-16 rounded-[4px] overflow-hidden max-w-full'>
                    <div className='w-full flex flex-col gap-3 bg-black/[47%] backdrop-blur-[45px] rounded-[4px] overflow-hidden'>
                      <div className='flex gap-2 items-center px-3 pt-3 max-w-full'>
                        <Image
                          className='w-[34px] h-[47px] rounded-[2px]'
                          width={34}
                          height={47}
                          alt='Book'
                          src={generateUrlForImage(
                            `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
                            `${Endpoints.getCoverImagesForBooks}/`,
                            `${showcaseShortVideo.bookId?._id}`,
                          )}
                        />
                        <div className='flex flex-col flex-1'>
                          <p className='text-sm/[22px] font-bold text-white line-clamp-1'>
                            {showcaseShortVideo?.bookId?.title}
                          </p>
                          <p className=' line-clamp-1 text-xs/[20px] text-white'>
                            {showcaseShortVideo?.bookId?.longDescription}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          router.push(
                            getUrl(
                              showcaseShortVideo?.bookId?.slug || '',
                              showcaseShortVideo?.bookId._id,
                            ),
                          )
                        }
                        className='h-[38px] bg-primary ps-3 pe-[16px] w-full rounded-none'
                      >
                        <div className='flex items-center justify-between w-full'>
                          <p className='uppercase text-sm/[22px] font-bold'>
                            VIEW BOOK
                          </p>
                          <div className='w-5 h-5 bg-white/40 flex items-center justify-center rounded-full'>
                            <BiChevronRight className='w-[18px] h-[18px] text-white' />
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Carousel
            ref={carouselRef}
            responsive={responsive}
            infinite={false}
            rewind={false}
            slidesToSlide={1}
            arrows
            // customRightArrow={<Right show={true} />}
            // customLeftArrow={<Left show={true} />}
            afterChange={(_, { currentSlide }) => {
              const newIndex = currentSlide - 1;
              setActiveSlide(newIndex);
              updateBytesQueryParam(newIndex);
            }}
            customDot={<CustomDot />}
            ssr
            itemClass='flex items-center'
            className='overflow-visible'
            centerMode={twScreen.lg}
          >
            {videosWithNull.map((item, index) => {
              if (!item) return <div key='null'></div>;
              const showcaseShortVideo = item;
              const isActive = index - 1 === activeSlide;
              const rating = showcaseShortVideo?.bookId?.rating || 0;
              const productId = showcaseShortVideo?.bookId?.hardcoverProductId;
              const digitalProductId =
                showcaseShortVideo?.bookId?.digitalProductId;
              const pricing = productId ? productPricingMap[productId] : null;
              const fullStars = Math.floor(rating);
              const hasHalfStar = rating % 1 !== 0;
              return (
                <div
                  key={showcaseShortVideo._id}
                  className={twMerge(
                    'flex w-full  transition-all rounded-lg overflow-hidden',
                    isActive ? 'scale-100 opacity-100 z-10 ' : 'scale-50 ',
                    !isActive && activeSlide - 1 === index
                      ? ''
                      : !isActive && activeSlide + 1 === index
                        ? ''
                        : '',
                  )}
                >
                  <div className='flex-1 relative bg-black '>
                    {isActive ? (
                      <iframe
                        key={`${showcaseShortVideo.youtubeId}-${index}-${isActive}`}
                        src={getEmbedUrl(showcaseShortVideo.youtubeId, true)}
                        className='w-full h-full '
                        allow='autoplay; encrypted-media; picture-in-picture'
                        allowFullScreen
                        title={
                          showcaseShortVideo.bookId?.title || 'Short video'
                        }
                      />
                    ) : (
                      <img
                        src={getThumbnailUrl(showcaseShortVideo.youtubeId)}
                        alt={
                          showcaseShortVideo.bookId?.title || 'Video thumbnail'
                        }
                        className='w-full h-full min-h-[100vh] object-cover'
                      />
                    )}
                  </div>

                  <RenderIf condition={isActive}>
                    <div className='flex-1 relative bg-grayZl-925  '>
                      <button
                        onClick={onClose}
                        className='absolute top-0 right-0 m-3 w-7 h-7 rounded-full bg-grayZl-500 flex items-center justify-center'
                      >
                        <BiX className='w-[18px] h-[18px] text-black' />
                      </button>
                      <div className='flex flex-col items-center'>
                        <Image
                          className='w-[168px] h-[238px] mt-10 mb-4'
                          width={168}
                          height={238}
                          alt='Book'
                          src={generateUrlForImage(
                            `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
                            `${Endpoints.getCoverImagesForBooks}/`,
                            `${showcaseShortVideo.bookId?._id}`,
                          )}
                        />
                        <Button
                          onClick={() =>
                            router.push(
                              getUrl(
                                showcaseShortVideo?.bookId?.slug || '',
                                showcaseShortVideo?.bookId._id,
                              ),
                            )
                          }
                          size={0}
                          variant={ButtonVariants.PRIMARY}
                          className='mb-5 border border-white/5 rounded-lg w-[232px] h-[32px] flex items-center justify-center uppercase font-bold text-[10px]'
                        >
                          VIEW BOOK
                        </Button>
                        <p className='text-sm uppercase text-white mb-2.5 px-4 line-clamp-1'>
                          {showcaseShortVideo?.bookId?.authors?.[0]
                            ? 'BY ' + showcaseShortVideo?.bookId?.authors?.[0]
                            : ''}
                        </p>
                        {/* <div className='flex items-center gap-[6px] mb-5'>
                        <Image
                          className='w-4 h-4 rounded-full object-cover'
                          width={16}
                          height={16}
                          alt='Company'
                          src={generateUrlForImage(
                            `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
                            `${Endpoints.getCoverImagesForBooks}/`,
                            `${showcaseShortVideo.bookId?._id}`,
                          )}
                        />
                        <p className='text-sm uppercase text-white'>
                          HR VP of Reliance Jio
                        </p>
                      </div> */}
                        <p className='text-[18px]/[26px] font-semibold text-white mb-3 text-center line-clamp-1 px-4'>
                          {showcaseShortVideo?.bookId?.title}
                        </p>
                        <p className='line-clamp-3 text-base text-white mb-[25px] text-center px-4'>
                          {showcaseShortVideo?.bookId?.longDescription}
                        </p>
                        <RenderIf condition={rating ? true : false}>
                          <div className='gap-[6px] flex items-center mb-[34px]'>
                            <div className='flex gap-1'>
                              {/* Full stars */}
                              {Array.from({ length: fullStars }).map(
                                (_, index) => (
                                  <BiSolidStar
                                    key={`full-${index}`}
                                    className='text-[#2DD598] w-4 h-4'
                                  />
                                ),
                              )}

                              {/* Half star */}
                              {hasHalfStar && (
                                <BiSolidStarHalf className='text-[#2DD598] w-4 h-4' />
                              )}
                            </div>
                            <p className='tet-[13px] font-semibold'>{rating}</p>
                          </div>
                        </RenderIf>
                        <div className='w-full flex gap-2.5 px-4 py-3 border-t border-t-grayZl-850 '>
                          <Button
                            loading={
                              Number(productId) === loadingAddToCart.id &&
                              loadingAddToCart.value
                            }
                            disabled={
                              Number(productId) === loadingAddToCart.id &&
                              loadingAddToCart.value
                            }
                            size={0}
                            variant={ButtonVariants.UNSTYLED}
                            className='px-3 py-2 bg-white rounded-lg flex-1'
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log({ pricing, showcaseShortVideo })
                              handleAddDataToCart(
                                {
                                  id: Number(productId) as number,
                                  temp_user_id: localUserId,
                                  quantity: 1,
                                  trackingData: {
                                    productData: {
                                      price: Number(pricing?.main_price || 0),
                                      id: Number(productId),
                                      heading: showcaseShortVideo.bookId.title,
                                      quantity: 1,
                                      currency: (
                                        getStoredCountryData()?.currency || 'inr'
                                      )?.toLowerCase(),
                                      category: "readers > get > /api/v2/showcase-short-video missing"

                                    },
                                    userData,
                                    pathname: router.pathname,
                                    productType: 'hardcopy',
                                    type: 'books',
                                  },
                                },
                                'hardCopy',
                                showcaseShortVideo.bookId.title,
                              );
                            }}
                          >
                            <div className='flex flex-col gap-1 items-center'>
                              <p className='text-center text-xs font-semibold text-black w-full line-clamp-1'>
                                Hardcopy + Digital
                              </p>
                              <div className='flex items-center gap-1'>
                                <p className='text-black text-base font-semibold'>
                                  {pricing?.currency_symbol}{' '}
                                  {pricing?.main_price || '--'}
                                </p>
                                {pricing?.stroked_price && (
                                  <p className='text-sm font-medium text-grayZl-700 line-through'>
                                    {pricing?.stroked_price}
                                  </p>
                                )}
                              </div>
                            </div>
                          </Button>
                          <Button
                            loading={
                              Number(digitalProductId) ===
                              loadingAddToCart.id && loadingAddToCart.value
                            }
                            disabled={
                              Number(digitalProductId) ===
                              loadingAddToCart.id && loadingAddToCart.value
                            }
                            size={0}
                            variant={ButtonVariants.UNSTYLED}
                            className='px-3 py-2 bg-grayZl-900 rounded-lg flex-1'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddDataToCart(
                                {
                                  id: Number(digitalProductId) as number,
                                  temp_user_id: localUserId,
                                  quantity: 1,
                                  trackingData: {
                                    productData: {
                                      price: Number(pricing?.main_price || 0),
                                      id: Number(digitalProductId),
                                      heading: showcaseShortVideo.bookId.title,
                                      quantity: 1,
                                      currency: (
                                        getStoredCountryData()?.currency || 'inr'
                                      )?.toLowerCase(),
                                      category: "readers > get > /api/v2/showcase-short-video missing"
                                    },
                                    userData,
                                    pathname: router.pathname,
                                    productType: 'digitalcopy',
                                    type: 'books',
                                  },
                                },
                                'digitalCopy',
                                showcaseShortVideo.bookId.title,
                              );
                            }}
                          >
                            <div className='flex flex-col gap-1 items-center'>
                              <p className='text-center text-xs font-semibold text-white w-full'>
                                Digital Only
                              </p>
                              <div className='flex items-center gap-1'>
                                <p className='text-white text-base font-semibold'>
                                  {pricing?.currency_symbol}
                                  {pricing?.digital_price || '--'}
                                </p>
                                {pricing?.stroked_price && (
                                  <p className='text-sm font-medium text-grayZl-700 line-through'>
                                    {pricing?.digital_stroked_price}
                                  </p>
                                )}
                              </div>
                            </div>
                          </Button>
                        </div>
                        <div className='flex gap-2 items-center pb-1'>
                          <SecurePaymentV2 />
                          <p className='text-xs/[20px] text-white'>
                            Secure Payments
                          </p>
                        </div>
                      </div>
                    </div>
                  </RenderIf>
                </div>
              );
            })}
          </Carousel>
        )}
      </div>
    </div>
  );
};

export default ShortVideoModel;
